import { css } from '@eti/styles';
import { Button, Card, Heading, InfoButton, Inline, Stack, vars } from '@etg/wings';
import { Trash } from '@phosphor-icons/react';
import { useProperty, useTranslation } from '@eti/providers';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import type { TripType, TravelerNew, TravelerPrice } from '@eti/schema-types';
import { price } from '@eti/utils';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../../../utils/carriers';
import { useToggle } from '../../../../../../common/custom-hooks/toggle';
import { getFinalSegments } from '../itinerary/utils';
import ItineraryDetailsLocationTime from '../itinerary/ItineraryDetailsLocationTime';
import { TripDetails } from '../tripDetails/TripDetails';
import type { TicketPriceModalInfoProps } from '../model';
import { TicketPriceInfoModal } from '../ticket/common/TicketPriceInfoModal';
import { getTravelerPrices } from '../../../../../../utils/travelers';
import { useGetTickets } from '../hooks/useGetTickets';
import type { TripDataType } from '../../../../../../common/custom-hooks/save-trips/models';
import type { PreSelectedExtraProducts } from '../../../../models';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../../../utils/carriersUtils';
import { getSelfTransferPlaces } from '../../utils/trip';
import { getOriginAndDestination } from './utils';

const removeTripStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  height: 24px;
  padding: 0;
`;

const savedTripCardStyle = css`
  padding-top: 0;
`;

const cardTitleWrapperStyle = css`
  background: ${vars.colors.divider};
  border-top-left-radius: ${vars.card.borderRadius};
  border-top-right-radius: ${vars.card.borderRadius};
  justify-content: space-between;
  margin-left: -16px;
  padding: 8px 16px;
  width: calc(100% + 32px);
`;

const cardTitleStyle = css`
  font-size: 0.875rem;
  line-height: normal;
`;

const dividerStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  margin: 0 -16px;
`;

interface SavedTripCardProps {
  removeSavedTrip: (tripKey: string) => void;
  setIsItineraryDrawerSelfTransferModalOpen: Dispatch<SetStateAction<boolean>>;
  trip: TripDataType;
}

export const SavedTripCard = ({
  trip,
  setIsItineraryDrawerSelfTransferModalOpen,
  removeSavedTrip,
}: SavedTripCardProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isOpen, handleOpen, handleClose } = useToggle();
  const {
    availableExtraProducts,
    bounds,
    isVI,
    paymentMethodPrices,
    shareableUrl,
    selectionKey,
    totalPrice,
    type,
    travelerPrices: resultTravelerPrices,
    travelerPricesWithoutPaymentDiscounts: travelerPricesWithoutDiscount,
    tripTravelers,
    voucherAmount,
  } = trip;

  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const segments = getFinalSegments(bounds);

  const { destination, origin } = getOriginAndDestination(bounds, type);

  const selectSavedTrip = (url: string) => {
    window.location.href = url;
  };

  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');

  const infoButtonLabel = t('ScreenReader.PriceSpecification.InfoButton');

  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  const travelerPrices = getTravelerPrices(
    tripTravelers as TravelerNew[],
    resultTravelerPrices as TravelerPrice[],
  );
  const travelerPriceBreakdown = getTravelerPrices(
    tripTravelers,
    travelerPricesWithoutDiscount as TravelerPrice[],
  );

  const discountAmount = voucherAmount?.value ?? 0;

  const preSelectedExtraProducts: PreSelectedExtraProducts[] = [];

  const ticketPriceModalInfo = {
    discountAmount,
    marketingCarrierNames,
    paymentMethodPrices,
    preSelectedExtraProducts,
    travelerPriceBreakdown,
    travelerPrices,
  } as TicketPriceModalInfoProps;

  const ticket = useGetTickets(
    availableExtraProducts,
    bounds,
    preSelectedExtraProducts,
    travelerPrices,
    discountAmount,
  )[0];

  const [isHovered, setIsHovered] = useState(false);

  const savedTripHeading = `${origin.code} ${origin.cityName} - ${destination.code} ${destination.cityName}`;

  const SRPriceAnnouncement = `${price.replaceWithCurrencyName(totalPrice)} ${t(
    'Result.PricePerAdult.Text',
  )}`;

  return (
    <Card className={savedTripCardStyle}>
      <Stack>
        <Inline alignY="center" className={cardTitleWrapperStyle}>
          <Heading className={cardTitleStyle} level={3}>
            {savedTripHeading}
          </Heading>
          <button
            aria-label={t('ScreenReader.Result.SavedTrip.Drawer.DeleteButton.Label')}
            className={removeTripStyles}
            data-testid="remove-saveTrip"
            onClick={() => removeSavedTrip(selectionKey)}
            type="button"
          >
            <Trash
              onMouseEnter={() => setIsHovered(!isHovered)}
              onMouseLeave={() => setIsHovered(!isHovered)}
              size={24}
              weight={isHovered ? 'fill' : 'light'}
            />
          </button>
        </Inline>
        <div>
          {segments.map((segment, index) => (
            <ItineraryDetailsLocationTime
              key={segment.segmentId}
              segment={segment}
              selfTransferPlaces={getSelfTransferPlaces(bounds, index)}
              setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
              tripType={type}
            />
          ))}
        </div>
        <TripDetails bounds={bounds} tripType={type as TripType} />
        <span className={dividerStyles} />
        <Stack spacing={8}>
          <Stack spacing={4}>
            <Inline align="end" alignY="center" spacing={4}>
              <InfoButton label={infoButtonLabel} onClick={handleOpen} />
              <TicketPriceInfoModal
                handleClose={handleClose}
                isOpen={isOpen}
                isRyanAirTicketingOrMarketingCarrierPresent={
                  isRyanAirTicketingOrMarketingCarrierPresent
                }
                isVI={isVI}
                ticket={ticket}
                ticketPriceModalInfo={ticketPriceModalInfo as TicketPriceModalInfoProps}
              />
              <Heading
                aria-label={SRPriceAnnouncement}
                data-testid="savedTrip-totalPrice"
                level={2}
              >
                {totalPrice}
              </Heading>
            </Inline>
            <Inline align="end" aria-hidden>
              {t('Result.PricePerAdult.Text')}
            </Inline>
          </Stack>
          <Button
            data-testid="saveTrip-selectButton"
            onClick={() => selectSavedTrip(shareableUrl)}
            size="large"
            type="submit"
            variant="primary"
          >
            {t('Result.BookButton.Default.Text')}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};
